import { createContext, memo, type ReactNode, useContext, useMemo } from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

import { type WorkspaceAgentMessageFeedbackRating } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/feedback/WorkspaceAgentMessageFeedbackActions';
import { isDevOrTestStage } from '~/swr-hooks/utils';

export type PrivateWorkspaceAgentMessageListContextValue = {
  latestAssistantMessageId: string | null;
  messageRatings: Readonly<Record<string, WorkspaceAgentMessageFeedbackRating>>;
  onRateMessage: (messageId: string, rating: WorkspaceAgentMessageFeedbackRating) => void;
  /** Keeps the virtual row's top edge in view after in-flow growth (e.g. uncited scope disclosure). */
  scrollDisplayRowStartIntoView: (displayRowId: string) => void;
  streamingMessageId: string | null;
};

const defaultContextValue: PrivateWorkspaceAgentMessageListContextValue = {
  latestAssistantMessageId: null,
  messageRatings: {},
  onRateMessage: () => {},
  scrollDisplayRowStartIntoView: () => {},
  streamingMessageId: null,
};

const PrivateWorkspaceAgentMessageListContext =
  createContext<PrivateWorkspaceAgentMessageListContextValue>(defaultContextValue);

export type PrivateWorkspaceAgentMessageListProviderProps = PrivateWorkspaceAgentMessageListContextValue & {
  children: ReactNode;
};

export const PrivateWorkspaceAgentMessageListProvider = memo(
  ({
    children,
    latestAssistantMessageId,
    messageRatings,
    onRateMessage,
    scrollDisplayRowStartIntoView,
    streamingMessageId,
  }: PrivateWorkspaceAgentMessageListProviderProps) => {
    const value = useMemo(
      (): PrivateWorkspaceAgentMessageListContextValue => ({
        latestAssistantMessageId,
        messageRatings,
        onRateMessage,
        scrollDisplayRowStartIntoView,
        streamingMessageId,
      }),
      [latestAssistantMessageId, messageRatings, onRateMessage, scrollDisplayRowStartIntoView, streamingMessageId],
    );

    return (
      <PrivateWorkspaceAgentMessageListContext.Provider value={value}>
        {children}
      </PrivateWorkspaceAgentMessageListContext.Provider>
    );
  },
);

PrivateWorkspaceAgentMessageListProvider.displayName = 'PrivateWorkspaceAgentMessageListProvider';

export const usePrivateWorkspaceAgentMessageListContext = (): PrivateWorkspaceAgentMessageListContextValue => {
  const value = useContext(PrivateWorkspaceAgentMessageListContext);

  if (value === defaultContextValue) {
    const error = new Error(
      'usePrivateWorkspaceAgentMessageListContext must be used within PrivateWorkspaceAgentMessageListProvider',
    );

    if (isDevOrTestStage()) {
      throw error;
    }

    reportErrorToBugsnag({
      context: 'PrivateWorkspaceAgentMessageListContext not found',
      error,
    });
  }

  return value;
};
